import { QuestionResponse_CandidateTestResponsesFragment } from "~/__generated__/graphql"
import { Download, File } from "lucide-react"
import DOCFileIcon from "~/images/icons/doc_file.svg?react"
import DOCXFileIcon from "~/images/icons/docx_file.svg?react"
import PPTFileIcon from "~/images/icons/ppt_file.svg?react"
import PPTXFileIcon from "~/images/icons/pptx_file.svg?react"
import XLSFileIcon from "~/images/icons/xls_file.svg?react"
import XLSXFileIcon from "~/images/icons/xlsx_file.svg?react"
import PDFFileIcon from "~/images/icons/pdf_file.svg?react"
import { Button } from "~/shadcn/ui/button"

export const DocumentQuestionResponse = ({
  questionResponse,
}: {
  questionResponse: QuestionResponse_CandidateTestResponsesFragment
}) => {
  const fileName = questionResponse.documentUrl?.split("/").pop()
  const fileExt = fileName?.split(".").pop()?.toLowerCase()

  const getFileIcon = (extension: string) => {
    switch (extension) {
      case "pdf":
        return <PDFFileIcon width={45} height={45} />
      case "doc":
        return <DOCFileIcon width={45} height={45} />
      case "docx":
        return <DOCXFileIcon width={45} height={45} />
      case "xls":
        return <XLSFileIcon width={45} height={45} />
      case "xlsx":
        return <XLSXFileIcon width={45} height={45} />
      case "ppt":
        return <PPTFileIcon width={45} height={45} />
      case "pptx":
        return <PPTXFileIcon width={45} height={45} />
      default:
        return <File width={45} height={45} />
    }
  }

  const getFileTypeName = (extension: string) => {
    switch (extension) {
      case "pdf":
        return "PDF document"
      case "doc":
      case "docx":
        return "Word document"
      case "xls":
      case "xlsx":
        return "Excel spreadsheet"
      case "ppt":
      case "pptx":
        return "PowerPoint presentation"
      default:
        return "Document"
    }
  }

  const cleanFileName = (name: string) => {
    try {
      const decodedName = decodeURIComponent(name)
      const nameWithoutExtension = decodedName.split(".").slice(0, -1).join(".")
      return nameWithoutExtension.replace(/[^a-zA-Z0-9]/g, "_")
    } catch (e) {
      return name
        .replace(/%20/g, "_")
        .replace(/[^a-zA-Z0-9.]/g, "_")
        .split(".")
        .slice(0, -1)
        .join(".")
    }
  }

  const handleDownload = () => {
    if (!questionResponse.documentUrl) return

    const link = document.createElement("a")
    link.href = questionResponse.documentUrl

    const extension = fileName?.split(".").pop() || ""
    const cleanedName = fileName
      ? `${cleanFileName(fileName)}.${extension}`
      : "document"

    link.download = cleanedName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <div className="text-sm text-gray-999 mt-4 mb-2">Answer</div>
      <div className="border border-gray-E6E6E3 rounded p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {fileExt ? getFileIcon(fileExt) : "Unknown"}
          </div>
          <div className="flex flex-col w-full">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">
                  {fileName ? cleanFileName(fileName) : "Unknown"}
                </span>
                <span className="text-xs text-gray-500">
                  {fileExt ? getFileTypeName(fileExt) : "Unknown"}
                  {" - "}
                  {questionResponse.documentSize}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2.5 mr-2.5">
            <Button size="sm" variant="outline-gray" onClick={handleDownload}>
              <Download size={16} />
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
